export interface IAccountManagementPackageHistoryItem {
  subscriptionId: number;
  purchaseType: string;
  startDate: string;
  endDate: string;
  createdAt: string;
  packagePrice?: number;
  paidAmount?: number;
  couponUsed: boolean;
  couponName?: string;
  couponDiscount?: number;
  newUserDiscount: boolean;
  discounted: boolean;
  userInitiated: boolean;
  isLast: boolean;
  autoRenewalEnabled: boolean;
}

export interface IAccountManagementCustomer {
  id: number;
  email: string;
  fullName?: string;
  mongerlabUserId?: number;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
  packageActive?: boolean;
  packageEndDate?: string;
  hi5SyncError?: boolean;
  packageHistory?: IAccountManagementPackageHistoryItem[];
  ordersLastSyncedAt?: string;
  ordersSyncError?: string;
  orderCount?: number;
  revenue?: number;
  warehouseCost?: number;
  productCost?: number;
  totalCost?: number;
  profit?: number;
  profitPercent?: number;
  assignedMembers?: IAccountManagementAssignableMember[];
}

export interface IAccountManagementAssignableMember {
  id: number;
  username: string;
  firstName?: string;
  lastName?: string;
}

/** Sipariş durumu enum değerleri (backend ile aynı). */
export type AccountManagementOrderStatusValue =
  | "COMPLETED"
  | "REFUND_WITH_RETURN"
  | "REFUNDED_WITHOUT_RETURN"
  | "A_TO_Z_REFUNDED"
  | "CANCELLED"
  | "REFUNDED_COM"
  | "SHIPPED"
  | "IN_TRANSIT"
  | "FBA_ORDER"
  | "PENDING";

export const ACCOUNT_MANAGEMENT_ORDER_STATUSES: AccountManagementOrderStatusValue[] = [
  "COMPLETED",
  "REFUND_WITH_RETURN",
  "REFUNDED_WITHOUT_RETURN",
  "A_TO_Z_REFUNDED",
  "CANCELLED",
  "REFUNDED_COM",
  "SHIPPED",
  "IN_TRANSIT",
  "FBA_ORDER",
  "PENDING",
];

export const ACCOUNT_MANAGEMENT_ORDER_STATUS_LABELS: Record<
  AccountManagementOrderStatusValue,
  string
> = {
  COMPLETED: "Completed",
  REFUND_WITH_RETURN: "Refund with Return",
  REFUNDED_WITHOUT_RETURN: "Refunded without Return",
  A_TO_Z_REFUNDED: "A to Z Refunded",
  CANCELLED: "Cancelled",
  REFUNDED_COM: "Refunded COM",
  SHIPPED: "Shipped",
  IN_TRANSIT: "In Transit",
  FBA_ORDER: "FBA Order",
  PENDING: "Pending",
};

export const REFUND_OR_CANCEL_ORDER_STATUSES: AccountManagementOrderStatusValue[] = [
  "REFUND_WITH_RETURN",
  "REFUNDED_WITHOUT_RETURN",
  "A_TO_Z_REFUNDED",
  "CANCELLED",
  "REFUNDED_COM",
];

export interface IAccountManagementProfitLossFilter {
  purchaseDateFrom?: string;
  purchaseDateTo?: string;
  orderStatus?: AccountManagementOrderStatusValue;
  shipmentStatus?: string;
  country?: string;
  hasNote?: boolean;
  search?: string;
}

export interface IAccountManagementCustomerListFilter {
  search?: string;
  periodStart?: string;
  periodEnd?: string;
}

export interface IAccountManagementCustomerNote {
  id: number;
  description: string;
  user?: {
    id: number;
    username: string;
    name?: string;
  };
  accountManagementCustomerId: number;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

/** Kar/zarar tablosunun bir satırı. Tutarlar sellerEarningLocal dışında USD. */
export interface IAccountManagementOrder {
  id: number;
  sellerOrderId: string;
  asin: string;
  country?: string;
  currencyCode?: string;
  purchaseDate?: string;
  warehouseOrderNo?: string;

  // API referans değerleri
  itemPriceUsd?: number;
  estimatedProfitUsd?: number;
  taxUsd?: number;
  shippingFeeUsd?: number;
  orderCostUsd?: number;

  // Elle girilen değerler
  sellerEarningLocal?: number;
  taxUsdOverride?: number;
  shippingCostUsdOverride?: number;
  productCostUsdOverride?: number;
  orderStatus?: string;
  orderNote?: string;
  shippingNote?: string;

  // Hesaplanan değerler
  fxRate?: number;
  fxRateDate?: string;
  revenueUsd?: number;
  effectiveTaxUsd?: number;
  warehouseCostUsd?: number;
  productCostUsd?: number;
  totalCostUsd?: number;
  profitUsd?: number;
  profitPercent?: number;

  shipmentStatus?: string;
  trackingUrl?: string;
  trackingNumber?: string;
  carrier?: string;
  latestDeliveryDate?: string;
  buyerName?: string;
  buyerPhone?: string;
  shippingAddress?: string;

  lastSyncedAt?: string;
}

export interface IAccountManagementProfitLossSummary {
  orderCount?: number;
  revenue?: number;
  tax?: number;
  warehouseCost?: number;
  productCost?: number;
  totalCost?: number;
  profit?: number;
  profitPercent?: number;
}

export interface IAccountManagementProfitLoss {
  orders: IAccountManagementOrder[];
  summary: IAccountManagementProfitLossSummary;
  lastSyncedAt?: string;
  syncError?: string;
}

/** Bir satırın elle girilebilen alanlarının tamamı; gönderilmeyen alan temizlenir. */
export interface IAccountManagementOrderUpdate {
  sellerEarningLocal?: number | null;
  taxUsdOverride?: number | null;
  shippingCostUsdOverride?: number | null;
  productCostUsdOverride?: number | null;
  orderStatus?: AccountManagementOrderStatusValue | string | null;
  orderNote?: string | null;
  shippingNote?: string | null;
  warehouseOrderNo?: string | null;
  shippingAddress?: string | null;
  clearFinancialsOnStatusChange?: boolean;
}

export interface IAccountManagementOrderUpdateResponse {
  order: IAccountManagementOrder;
  summary: IAccountManagementProfitLossSummary;
}

export interface IAccountManagementCustomerPaginatedResponse {
  data: IAccountManagementCustomer[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}
